import { _js } from '@ifixit/localize';
import { useState, useCallback } from 'react';
import { connect } from 'react-redux';
import styled, { css } from 'styled-components';
import { color } from '@ifixit/primitives';
import AccordionItem from './accordion_item';
import AccordionSubitem from './accordion_subitem';
import LanguageButton from '../dropdowns/language_button';
import LanguageDropdown from '../dropdowns/language_dropdown';
import { useMediaQuery } from 'react-responsive';
import { trackAnalyticsCustomEvent } from '@ifixit/analytics';
import { HeadingLinkType, SublinkType } from '../headings';
import { useTranslations } from '@ifixit/i18n/hooks';
import { FaIcon } from '@ifixit/icons';
import { faToolbox } from '@fortawesome/pro-solid-svg-icons';

const navHeight = '68px';

const Container = styled.div<{ $isOpen?: boolean }>`
   position: absolute;
   padding: 32px 16px 0;
   top: ${navHeight};
   left: 0;
   right: 0;
   height: calc(100vh - ${navHeight});
   background: ${color.gray[900]};
   opacity: 0;
   visibility: hidden;
   transform: translateX(-100%);
   transition: opacity 0.25s, transform 0.25s;
   overflow-x: hidden;
   overflow-y: auto;
   -webkit-overflow-scrolling: touch;
   z-index: 200;

   ${props =>
      props.$isOpen &&
      css`
         opacity: 1;
         visibility: visible;
         transform: translateX(0);
      `};
`;

const mapStateToProps = (state: any) => ({
   headerState: state.headerState,
});

const regionAccordionTitle = _js('Language / Region');

const MobileMenu = ({
   headerState,
   links,
   languageDropdown,
   isOnCart,
   onWorkbenchOpen,
}: {
   headerState: any;
   links: HeadingLinkType[];
   languageDropdown: any;
   isOnCart: boolean;
   onWorkbenchOpen: () => void;
}) => {
   const { displayMobileMenu } = headerState;

   const [activeId, setActiveId] = useState();

   const accordionItemClicked = useCallback(
      (clickedId: any) => {
         setActiveId(activeId === clickedId ? null : clickedId);
      },
      [activeId, setActiveId]
   );

   const { languages, translationPreferencesUrl } = languageDropdown;

   const selectedLanguage = languages.find((language: any) => language.isSelected);

   const fullWidthDropdown = useMediaQuery({ maxWidth: '450px' });
   const t = useTranslations('Workbench');

   return (
      <Container id="mobileMenuContainer" $isOpen={displayMobileMenu}>
         {links.map(
            (data: any) =>
               data.disabled || (
                  <AccordionItem
                     key={`accordion-item-${data.title}`}
                     title={data.title}
                     isActive={data.title === activeId}
                     onClick={accordionItemClicked}
                  >
                     {data.sublinks.map(
                        (sublink: SublinkType, index: any) =>
                           sublink.disabled || (
                              <AccordionSubitem
                                 href={sublink.url}
                                 key={`accordion-${data.title}-sublink-${index}`}
                                 title={sublink.title}
                              />
                           )
                     )}
                  </AccordionItem>
               )
         )}
         {App.siteName !== 'ifixit' && (
            <AccordionItem
               title={regionAccordionTitle}
               isActive={regionAccordionTitle === activeId}
               onClick={accordionItemClicked}
            >
               {!isOnCart && (
                  <AccordionSubitem
                     title={selectedLanguage.autoglottonym}
                     icon={<LanguageButton isoCode={selectedLanguage.isoCode} />}
                     fullWidthDropdown={fullWidthDropdown}
                     dropdown={
                        <LanguageDropdown
                           translationPreferencesUrl={translationPreferencesUrl}
                           languages={languages}
                           isLightTheme
                           mobile={fullWidthDropdown}
                        />
                     }
                     onMount={() => {
                        trackAnalyticsCustomEvent({
                           eventCategory: 'Language',
                           eventAction: 'Language Dropdown - Click',
                        });
                     }}
                  />
               )}
            </AccordionItem>
         )}
         <div className="flex items-center px-4 mt-8">
            <div
               onClick={onWorkbenchOpen}
               className="flex w-full cursor-pointer items-center py-3 text-sm font-light text-white"
               id="workbenchDropdownToggle"
            >
               <FaIcon icon={faToolbox} h="20px" color="white" mr={3} />
               {t('workbench')}
            </div>
         </div>
      </Container>
   );
};

export default connect(mapStateToProps)(MobileMenu);
